import { Trans, useTranslation } from "react-i18next";
import {
  Avatar,
  Button,
  Card,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useState } from "react";
import { foyerApi, type RadioStationRecord } from "../http/foyer";
import { friendlyError } from "../utils/apiError";
import { describeError, useToast } from "../components/toast";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { usePermission } from "../auth/access";
import AssetField from "../components/AssetField";
import { ItemFormSurface, type Dismiss } from "../components/EditDrawer";

/**
 * Admin-only global catalogue of Radio Stations. Non-admin users can still
 * GET the list (read-only), but only admins reach this page (nav item is
 * hidden) and the mutation endpoints reject anyone else with 403.
 *
 * Premises consume this catalogue: per-premise selection + overrides are
 * resolved against it in `shared/radio-merge.ts`.
 */
export default function RadioStationsPage() {
  const toast = useToast();
  const { t } = useTranslation(["channels", "common"]);
  // Mutation affordances follow the same permission the API enforces.
  const isAdmin = usePermission("tv-layout", "administrate", "nenda");
  const [items, setItems] = useState<RadioStationRecord[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    { mode: "create" } | { mode: "edit"; record: RadioStationRecord } | null
  >(null);

  const refresh = useCallback(async () => {
    try {
      const list = await foyerApi.listRadioStations();
      setItems(Array.isArray(list) ? list : []);
      setErr(null);
    } catch (e) {
      setErr(friendlyError(t, e));
    }
  }, [t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onDelete(row: RadioStationRecord) {
    if (
      !window.confirm(t("radioStationsPage.deleteConfirm", { name: row.name }))
    )
      return;
    try {
      await foyerApi.deleteRadioStation(row.id);
      toast.success(t("radioStationsPage.deleted", { name: row.name }));
      await refresh();
    } catch (e) {
      toast.error(
        describeError(t, "deleteRadioStation", e, { name: row.name })
      );
    }
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("radioStationsPage.eyebrow")}
        title={t("radioStationsPage.title")}
        actions={
          isAdmin && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => setDialog({ mode: "create" })}
              data-testid="radio-station-add"
            >
              {t("radioStationsPage.newStation")}
            </Button>
          )
        }
      />

      {err && (
        <Typography variant="body2" color="error">
          {err}
        </Typography>
      )}

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader data-testid="radio-stations-list">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }} />
                <TableCell>{t("common:labels.name")}</TableCell>
                <TableCell>{t("radioStationsPage.columns.tag")}</TableCell>
                <TableCell>
                  {t("radioStationsPage.columns.streamUrl")}
                </TableCell>
                {isAdmin && <TableCell sx={{ width: 96 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items?.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  data-testid={`radio-station-row-${row.id}`}
                >
                  <TableCell>
                    <Avatar
                      src={row.logoUrl ?? undefined}
                      variant="rounded"
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "grey.600",
                        fontSize: 14,
                      }}
                    >
                      {row.name.charAt(0)}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "fontWeightMedium" }}
                    >
                      {row.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.tag}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      title={row.streamUrl ?? ""}
                    >
                      {row.streamUrl ?? "-"}
                    </Typography>
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title={t("common:buttons.edit")}>
                        <IconButton
                          size="small"
                          aria-label={t("radioStationsPage.editNamed", {
                            name: row.name,
                          })}
                          onClick={() =>
                            setDialog({ mode: "edit", record: row })
                          }
                          data-testid={`radio-station-edit-${row.id}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("common:buttons.delete")}>
                        <IconButton
                          size="small"
                          aria-label={t("radioStationsPage.deleteNamed", {
                            name: row.name,
                          })}
                          onClick={() => onDelete(row)}
                          data-testid={`radio-station-delete-${row.id}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 5 : 4}>
                    <Typography variant="caption" color="text.secondary">
                      <Trans
                        t={t}
                        i18nKey="radioStationsPage.empty"
                        components={{ strong: <strong /> }}
                      />
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {dialog && (
        <RadioStationDialog
          initial={dialog.mode === "edit" ? dialog.record : null}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await refresh();
          }}
        />
      )}
    </PageContainer>
  );
}

function RadioStationDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: RadioStationRecord | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const { t } = useTranslation(["channels", "common"]);
  const [name, setName] = useState(initial?.name ?? "");
  const [tag, setTag] = useState(initial?.tag ?? "");
  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? "");
  const [streamUrl, setStreamUrl] = useState(initial?.streamUrl ?? "");
  const [metadataId, setMetadataId] = useState(initial?.metadataId ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(dismiss: Dismiss) {
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        name,
        tag,
        logoUrl: logoUrl.trim() || null,
        streamUrl: streamUrl.trim() || null,
        metadataId: metadataId.trim() || null,
      };
      if (initial) {
        await foyerApi.updateRadioStation(initial.id, payload);
        toast.success(t("radioStationsPage.updated", { name }));
      } else {
        await foyerApi.createRadioStation(payload);
        toast.success(t("radioStationsPage.added", { name }));
      }
      // Slide the form away first; the page then refreshes and unmounts it.
      dismiss(() => void onSaved());
    } catch (e) {
      setErr(friendlyError(t, e));
      toast.error(
        name.trim()
          ? describeError(t, "saveRadioStationNamed", e, { name: name.trim() })
          : describeError(t, "saveRadioStation", e)
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <ItemFormSurface
      mode={initial ? "edit" : "create"}
      onClose={onClose}
      title={
        initial
          ? t("radioStationsPage.editStation")
          : t("radioStationsPage.newStation")
      }
      maxWidth="xs"
      busy={busy}
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void save(dismiss)}
            disabled={busy || !name.trim()}
            data-testid="radio-station-save"
          >
            {busy ? t("common:states.saving") : t("common:buttons.save")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          label={t("radioStationsPage.fields.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          autoFocus
          placeholder={t("radioStationsPage.fields.namePlaceholder")}
          inputProps={{ "data-testid": "radio-station-name" }}
        />
        <TextField
          label={t("radioStationsPage.fields.tag")}
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          size="small"
          placeholder={t("radioStationsPage.fields.tagPlaceholder")}
        />
        <AssetField
          label={t("radioStationsPage.fields.logo")}
          value={logoUrl}
          onChange={setLogoUrl}
          preview
          testId="radio-station-logo"
        />
        <TextField
          label={t("radioStationsPage.fields.streamUrl")}
          value={streamUrl}
          onChange={(e) => setStreamUrl(e.target.value)}
          size="small"
          placeholder={t("radioStationsPage.fields.streamUrlPlaceholder")}
        />
        <TextField
          label={t("radioStationsPage.fields.metadataId")}
          value={metadataId}
          onChange={(e) => setMetadataId(e.target.value)}
          size="small"
        />
        {err && (
          <Typography variant="caption" color="error">
            {err}
          </Typography>
        )}
      </Stack>
    </ItemFormSurface>
  );
}
